import axios from "axios";
import React, { useEffect, useState } from "react";
import dayjs from "dayjs";

const VoucherSetupBill = ({ selectedAccount, onDataToSend, companyCode, jvType }) => {
  const COMPANY_CODE = companyCode || localStorage.getItem("companyCode") || "-";
  const today = new Date().toISOString().split("T")[0];
  const [fromDate, setFromDate] = useState(today);
  const [toDate, setToDate] = useState(today);

  const [accounts, setAccounts] = useState(null);
  const [amounts, setAmounts] = useState([]);
  const [selectedToggle, setSelectedToggle] = useState([]);
  const [accountsToTransfer, setAccountsToTransfer] = useState([]);
  const [sumOfAmounts, setSumOfAmounts] = useState(0);

  // Fetch accounts on component mount or when dependencies change
  useEffect(() => {
    fetchSetUpBillAccounts();
  }, [COMPANY_CODE, selectedAccount, fromDate, toDate]);

  // Update sum of amounts whenever amounts change
  useEffect(() => {
    if (Array.isArray(amounts)) {
      const totalAmount = amounts.reduce((acc, val) => {
        const num = parseFloat(val);
        return acc + (isNaN(num) ? 0 : num);
      }, 0);
      setSumOfAmounts(Number(totalAmount.toFixed(2)));
    } else {
      setSumOfAmounts(0);
    }
  }, [amounts]);

  // Initialize amounts array when accounts are fetched
  useEffect(() => {
    if (accounts) {
      setAmounts(new Array(accounts.length).fill(""));
      setSelectedToggle(new Array(accounts.length).fill(false));
    }
  }, [accounts]);

  const fetchSetUpBillAccounts = async () => {
    let apiPaths_from_local_storage = localStorage.getItem("apiPaths")
      ? JSON.parse(localStorage.getItem("apiPaths"))
      : "-";

    let billType = "RECEIPTS";
    if (jvType?.startsWith("PV")) {
      billType = "PAYMENTS";
    } else if (jvType?.startsWith("RV")) {
      billType = "RECEIPTS";
    }

    let apiUrl = `${apiPaths_from_local_storage[0].API_CONFIG}/api/CRMOutstandingRVPV/${COMPANY_CODE}/${billType}/${fromDate}/${toDate}/${selectedAccount}/-/-/-`;
    console.log("Setup Bill URL", apiUrl)
    try {
      const response = await axios.get(apiUrl);
      const data = response.data;
      console.log("fetchOutstandingRV List:", data);
      setAccounts(data);
    } catch (err) {
      console.error("Error while fetchOutstandingRV", err);
    }
  };

  const handleAmountChange = (index, value) => {
    const updatedAmounts = [...amounts];
    updatedAmounts[index] = Number(value) || 0; // Ensure number
    setAmounts(updatedAmounts);

    if (selectedToggle[index]) {
      setAccountsToTransfer((prev) => {
        const updated = [...prev];
        const accountIndex = updated.findIndex(
          (acc) => acc.INV === accounts[index].INV,
        );
        if (accountIndex !== -1) {
          updated[accountIndex] = {
            ...updated[accountIndex],
            AMOUNT: updatedAmounts[index],
          };
        }
        return updated;
      });
    }
  };

  const handleSelectToggle = (index, checked) => {
    setSelectedToggle((prev) => {
      const updated = [...prev];
      updated[index] = checked;
      return updated;
    });

    setAmounts((prev) => {
      const updated = [...prev];
      updated[index] = checked
        ? Number(accounts[index]?.BalAmt || accounts[index]?.BALAMT * accounts[index]?.RATE)
        : "";
      return updated;
    });

    if (checked) {
      const selectedAcc = {
        ...accounts[index],
        AMOUNT:
          amounts[index] ||
          Number(accounts[index]?.BalAmt || accounts[index]?.BALAMT * accounts[index]?.RATE) ||
          0,
      };
      setAccountsToTransfer((prev) => [...prev, selectedAcc]);
    } else {
      setAccountsToTransfer((prev) =>
        prev.filter((acc) => acc.INV !== accounts[index].INV),
      );
    }
  };

  const onTransfer = () => {
    console.log("Accounts to transfer:", accountsToTransfer);
    onDataToSend(accountsToTransfer);
  };

  return (
    /* Changed to flex layout with a defined height so it behaves correctly as a dashboard widget */
    <div className="h-[600px] max-w-9xl mx-auto flex flex-col justify-between p-2">

      {/* 1. Scrollable Table Container */}
      <div className="flex-grow overflow-y-auto border rounded mb-4 bg-gray-100 font-normal">
        <table className="relative w-full text-sm border-collapse">
          <thead className="sticky top-0 z-20 bg-gray-100">
            <tr>
              <th className="font-normal border px-2 py-1">Amount</th>
              <th className="font-normal border px-2 py-1">Select</th>
              <th className="font-normal border px-2 py-1">Bal Amount</th>
              <th className="font-normal border px-2 py-1">Ref</th>
              <th className="font-normal border px-2 py-1">Inv Date</th>
              <th className="font-normal border px-2 py-1">Cur</th>
              <th className="font-normal border px-2 py-1">Rate</th>
              <th className="font-normal border px-2 py-1">FC Amount</th>
              <th className="font-normal border px-2 py-1">Dept No</th>
            </tr>
          </thead>
          <tbody>
            {accounts?.length > 0 ? (
              accounts.map((acc, index) => (
                <tr key={acc?.INV || index} className="text-sm font-thin">
                  <td className="border w-24 bg-white">
                    <input
                      type="number"
                      value={amounts[index] ?? ""}
                      onChange={(e) =>
                        handleAmountChange(index, e.target.value)
                      }
                      className="w-full border-none text-right rounded font-semibold text-sm"
                    />
                  </td>
                  <td className="border w-24 bg-white text-center">
                    <label className="relative inline-block w-[3.5em] h-[2em]">
                      <input
                        type="checkbox"
                        className="peer sr-only"
                        checked={!!selectedToggle[index]}
                        onChange={(e) =>
                          handleSelectToggle(index, e.target.checked)
                        }
                      />
                      <span
                        className="
                          absolute cursor-pointer inset-0 bg-gray-300 rounded-full transition-all duration-300 ease-in-out
                          after:content-[''] after:absolute after:top-[4px] after:left-[4px] 
                          after:w-[1.5em] after:h-[1.5em] after:bg-white after:rounded-full
                          after:transition-all after:duration-300 after:ease-in-out
                          after:shadow-[10px_0_40px_rgba(0,0,0,0.1)]
                          peer-checked:bg-blue-500
                          peer-checked:after:translate-x-[1.5em]
                          peer-checked:after:shadow-[-10px_0_40px_rgba(0,0,0,0.1)]
                          peer-active:after:w-[3em]
                          peer-checked:peer-active:after:translate-x-[0.5em]
                        "
                      ></span>
                    </label>
                  </td>
                  <td className="border bg-white px-2 py-1 text-right">
                    {acc?.BalAmt || acc?.BALAMT}
                  </td>
                  <td className="border bg-white px-2 py-1 text-center">
                    {acc?.INV ?? "-"}
                  </td>
                  <td className="border bg-white px-2 py-1 text-center">
                    {acc?.INVDATE
                      ? dayjs(acc.INVDATE).format("DD/MM/YYYY")
                      : "-"}
                  </td>
                  <td className="border bg-white px-2 py-1 text-right">
                    {acc?.CUR ?? "-"}
                  </td>
                  <td className="border bg-white px-2 py-1 text-right">
                    {acc?.RATE ?? "-"}
                  </td>
                  <td className="border bg-white px-2 py-1 text-center">
                    {acc?.FCBalAmt ?? "-"}
                  </td>
                  <td className="border bg-white px-2 py-1 text-center">
                    {acc?.DEPTNO ?? "-"}
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={9} className="text-center py-4 text-gray-500">
                  No outstanding data available
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* 2. Sticky Footer Bar */}
      <div className="flex justify-between items-center bg-white pt-2 border-t">
        <div>
          <p className="text-sm text-gray-700">
            Total:{" "}
            <span className="font-semibold text-blue-700 text-base">{sumOfAmounts}</span>
          </p>
        </div>
        <button
          onClick={onTransfer}
          type="button"
          className="bg-blue-500 text-center w-32 rounded-xl h-10 relative text-white text-base font-medium group flex-shrink-0"
        >
          <div className="bg-white rounded-lg h-8 w-1/4 flex items-center justify-center absolute left-1 top-[4.5px] group-hover:w-[132px] z-10 duration-500">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 487.2 487.2"
              className="h-4 w-4"
              fill="#4299e1"
            >
              <g>
                <path d="M46.694,222.8h142.1c22.2,0,40.3-18.1,40.3-40.3V40.3c0-22.2-18.1-40.3-40.3-40.3h-142.1c-22.2,0-40.3,18.1-40.3,40.3v142.1C6.294,204.7,24.394,222.8,46.694,222.8z" />
                <path d="M477.294,102.7l-79.1-79.1c-4.8-4.8-12.5-4.8-17.3,0s-4.8,12.5,0,17.3l58.2,58.2h-170.8c-6.8,0-12.3,5.5-12.3,12.3s5.5,12.3,12.3,12.3h170.8l-58.2,58.2c-4.8,4.8-4.8,12.5,0,17.3c2.4,2.4,5.5,3.6,8.7,3.6s6.3-1.2,8.7-3.6l79.1-79.1C482.094,115.2,482.094,107.5,477.294,102.7z" />
                <path d="M480.894,446.9V304.8c0-22.2-18.1-40.3-40.3-40.3h-142.2c-22.2,0-40.3,18.1-40.3,40.3v142.1c0,22.2,18.1,40.3,40.3,40.3h142.1C462.794,487.2,480.894,469.1,480.894,446.9z" />
                <path d="M97.594,467.2c3.1,0,6.3-1.2,8.7-3.6c4.8-4.8,4.8-12.5,0-17.3l-58.2-58.2h170.8c6.8,0,12.3-5.5,12.3-12.3s-5.5-12.3-12.3-12.3h-170.8l58.2-58.2c4.8-4.8,4.8-12.5,0-17.3s-12.5-4.8-17.3,0l-79.1,79.1c-4.8,4.8-4.8,12.5,0,17.3l79.1,79.1C91.394,466,94.494,467.2,97.594,467.2z" />
              </g>
            </svg>
          </div>
          <p className="translate-x-2">Transfer</p>
        </button>
      </div>
    </div>
  );
};

export default VoucherSetupBill;